/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Stack } from '@fluentui/react';
import {
  Button,
  Caption1Stronger,
  Caption1,
  mergeClasses,
  Divider,
} from '@fluentui/react-components';
import {
  EyeRegular,
  ThumbLikeRegular,
  ThumbDislikeRegular,
  ThumbLikeFilled,
  ThumbDislikeFilled,
} from '@fluentui/react-icons';
import {
  LineChart,
} from '@fluentui/react-charting';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';

import {
  PERFORMANCE_INSIGHTS_ISSUE,
  PERFORMANCE_INSIGHTS_TYPE,
  PERFORMANCE_INSIGHTS_POPUP_CONFIG,
  PERFORMANCE_INSIGHTS_ACTION,
} from './constants/consts';
import { getColor } from './util/util';

export const BudgetDetail = (props) => {
  const {
    classes,
    newI18n,
    allData,
    onClickSaveBudget,
    setIsBudgetLikeSelected,
    setIsBudgetDislikeSelected,
    isBudgetLikeSelected,
    isBudgetDislikeSelected,
    chartClasses,
  } = props;

  const budgetResult = [];
  budgetResult.push({
    legend: 'Actual',
    data: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actualBudgetPoints,
    color: getColor('Actual'),
    lineOptions: {
      strokeWidth: '1px',
    },
  });
  budgetResult.push({
    legend: 'Projected',
    legendShape: 'dottedLine',
    data: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].estimatedBudgetPoints,
    color: getColor('Projected'),
    lineOptions: {
      strokeDasharray: '2px',
      strokeDashoffset: '-1',
      strokeLinecap: 'butt',
      strokeWidth: '1px',
    },
  });

  const budgetChartData = {
    chartTitle: newI18n.getString(
      PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].chartTitle,
      allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET]
    ),
    lineChartData: budgetResult,
  };

  const colorFillBarData = [
    {
      legend: 'Time range 1',
      color: '#EEACB2',
      data: [
        {
          startX: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actualBudgetPoints[5].x,
          endX: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actualBudgetPoints[6].x,
        },
      ],
    },
  ];

  return (
    <Stack className={classes.healthCheck}>
      <Caption1Stronger className={classes.title}>
        {
          newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].title)
        }
      </Caption1Stronger>
      <Stack horizontal className={classes.body} tokens={{ childrenGap: 24 }}>
        <Stack>
          <Caption1 className={classes.chartTitle}>
            {
              newI18n.getString(_TL_('Impression between {{start}} to {{end}}'), { start: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].startDate, end: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].endDate })
            }
          </Caption1>
          <LineChart
            data={budgetChartData}
            legendsOverflowText="Overflow Items"
            className={mergeClasses('inline-chart', chartClasses.inlineChart, classes.chart)}
            allowMultipleShapesForPoints
            tickFormat="%m/%d"
            tickValues={_.map(allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actualBudgetPoints, point => point.x)}
            colorFillBars={colorFillBarData}
          />
          <Stack horizontal className={classes.bottom}>
            <EyeRegular className={classes.viewIcon} />
            <Caption1>
              {
                newI18n.getString(_TL_('The issue was detected on {{start}}'), { start: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].startDate })
              }
            </Caption1>
          </Stack>
        </Stack>
        <Stack className={classes.surface} tokens={{ childrenGap: 12 }}>
          <Caption1>
            {
              newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].description, allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET])
            }
          </Caption1>
          <Caption1>
            {
              newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].pitching)
            }
          </Caption1>
          <Divider className={classes.divider} />
          <Caption1Stronger>{newI18n.getString(_TL_('You can fix this issue by:'))}</Caption1Stronger>
          <Caption1>
            {
              newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actionDetails[0], allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET])
            }
          </Caption1>
          <Stack className={classes.action} horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
            <Button className={classes.saveButton} appearance="primary" onClick={() => onClickSaveBudget()}>{newI18n.getString(PERFORMANCE_INSIGHTS_ACTION.SAVE)}</Button>
            <LearnMoreLink
              linkText={newI18n.getString(_TL_('Learn more'))}
              topic="53050"
              isUnderlineLink={false}
            />
          </Stack>
          <Stack className="feedback" horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
            <Button
              className="feedbackButton"
              icon={isBudgetLikeSelected ? <ThumbLikeFilled /> : <ThumbLikeRegular />}
              appearance="transparent"
              onClick={() => {
                setIsBudgetLikeSelected(!isBudgetLikeSelected);
              }}
            />
            <Button
              className="feedbackButton"
              icon={isBudgetDislikeSelected ? <ThumbDislikeFilled /> : <ThumbDislikeRegular />}
              appearance="transparent"
              onClick={() => {
                setIsBudgetDislikeSelected(!isBudgetDislikeSelected);
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

BudgetDetail.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  allData: PropTypes.shape({}).isRequired,
  onClickSaveBudget: PropTypes.func.isRequired,
  setIsBudgetLikeSelected: PropTypes.func.isRequired,
  setIsBudgetDislikeSelected: PropTypes.func.isRequired,
  isBudgetLikeSelected: PropTypes.bool.isRequired,
  isBudgetDislikeSelected: PropTypes.bool.isRequired,
  chartClasses: PropTypes.shape({}).isRequired,
};
